import { useEffect, useMemo, useRef, useState } from 'react';
import type { FeatureFileUrl } from '@tradeoutlet/shared';
import { Lightbox, type GalleryImage } from '@/components/media/ImageGallery';
import DOMPurify from 'dompurify';
import type { ResolveFile } from './editor';

/**
 * Renders server-rendered (already whitelisted) rich HTML. DOMPurify runs again in the browser as defence in depth;
 * only sandboxed iframes from the three allowed video hosts survive. Uploads (data-attachment-id) are resolved to
 * short-lived URLs through the owning feature's endpoint.
 */
const EMBED_HOSTS = ['https://www.youtube-nocookie.com/embed/', 'https://player.vimeo.com/video/', 'https://web.microsoftstream.com/embed/'];
DOMPurify.addHook('uponSanitizeElement', (node, data) => {
  if (data.tagName !== 'iframe') return;
  const src = (node as Element).getAttribute('src') ?? '';
  if (!EMBED_HOSTS.some(h => src.startsWith(h)) && !/^https:\/\/[\w-]+\.sharepoint\.com\//.test(src)) node.parentNode?.removeChild(node);
});

export function sanitize(html: string) {
  return DOMPurify.sanitize(html, {
    ADD_TAGS: ['iframe', 'video'], ADD_ATTR: ['allowfullscreen', 'sandbox', 'loading', 'referrerpolicy', 'target', 'data-attachment-id', 'data-tone', 'data-provider', 'controls', 'preload'],
    FORBID_ATTR: ['style'],
  });
}

/**
 * Feedback 1 #7: with `file`, inline images use the server-resized thumb_url, keep their box from width/height
 * (set at save time), load lazily and open a lightbox (prev/next across the article's images, download).
 */
export function RichContent({ html, resolve, file, className = '' }: { html: string; resolve?: ResolveFile; file?: (id: string) => Promise<FeatureFileUrl | null>; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const clean = useMemo(() => sanitize(html), [html]);
  const [box, setBox] = useState<{ images: GalleryImage[]; index: number } | null>(null);
  const images = (): HTMLImageElement[] => [...(ref.current?.querySelectorAll<HTMLImageElement>('img[data-attachment-id]') ?? [])];
  const open = (target: EventTarget | null) => {
    if (!file || !(target instanceof HTMLImageElement) || !target.dataset.attachmentId) return false;
    const all = images();
    setBox({ images: all.map(i => ({ id: i.dataset.attachmentId!, file_name: i.alt || 'Image', width: i.naturalWidth || null, height: i.naturalHeight || null })), index: Math.max(0, all.indexOf(target)) });
    return true;
  };
  useEffect(() => {
    const root = ref.current;
    if (!root || !resolve) return;
    let on = true;
    root.querySelectorAll<HTMLElement>('[data-attachment-id]').forEach(el => {
      const id = el.dataset.attachmentId!;
      if (file && el instanceof HTMLImageElement) {
        el.loading = 'lazy'; el.decoding = 'async'; el.tabIndex = 0; el.setAttribute('role', 'button'); el.classList.add('cursor-zoom-in');
        void file(id).then(f => { if (on && f) el.src = f.thumb_url ?? f.url; }).catch(() => undefined);
        return;
      }
      void resolve(id).then(url => {
        if (!on || !url) return;
        if (el instanceof HTMLImageElement || el instanceof HTMLVideoElement) el.src = url;
        else if (el instanceof HTMLAnchorElement) { el.href = url; el.target = '_blank'; el.rel = 'noopener'; }
      }).catch(() => undefined);
    });
    return () => { on = false; };
  }, [clean, resolve, file]);
  return <>
    <div ref={ref} className={`rich-content ${className}`} dangerouslySetInnerHTML={{ __html: clean }}
      onClick={e => { if (open(e.target)) e.preventDefault(); }}
      onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && open(e.target)) e.preventDefault(); }} />
    {box && file && <Lightbox images={box.images} index={box.index} onClose={() => setBox(null)} original={async id => (await file(id))?.url ?? null} />}
  </>;
}
